import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';

export default function Klagevejledning() {
  const { lang } = useLanguage();

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Klagevejledning | Nordisk Psykiatri' 
    : 'Klagovägledning | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Information om hvordan du kan klage over behandling eller andre forhold ved Nordisk Psykiatri.'
    : 'Information om hur du kan klaga på behandling eller andra förhållanden på Nordisk Psykiatri.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      {/* HERO */}
      <FluidSubpageHero
        className="bg-[#f6f0e2]"
        style={{ backgroundColor: '#f6f0e2' }}
        eyebrow={lang === 'da' ? 'VILKÅR & POLITIKKER' : 'VILLKOR & POLICYER'}
        title={lang === 'da' ? 'Klagevejledning' : 'Klagovägledning'}
        description={lang === 'da' 
          ? 'Vi ønsker, at du føler dig tryg i dit forløb hos os. Skulle du mod forventning have anledning til at klage, tager vi det meget alvorligt.'
          : 'Vi vill att du ska känna dig trygg under din behandling hos oss. Skulle du mot förmodan ha anledning att klaga tar vi det på största allvar.'}
      />

      {/* Clean, Simple Terms Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          {/* Sections */}
          <div className="space-y-12">
            
            {/* Section 1: Hvordan klager jeg? */}
            <div className="text-left">
              <h2 className="font-display text-2xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? 'HVORDAN KLAGER JEG?' : 'HUR KLAGAR JAG?'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Hvis du ønsker at klage over en behandling eller anden forhold ved klinikken, opfordrer vi dig til først at kontakte os direkte. Det giver os mulighed for at forstå situationen og finde en løsning.'
                    : 'Om du vill klaga på en behandling eller andra förhållanden på kliniken uppmanar vi dig att först kontakta oss direkt. Det ger oss möjlighet att förstå situationen och hitta en lösning.'}
                </p>
                <p>
                  {lang === 'da' ? 'Du kan klage via telefon eller mail:' : 'Du kan klaga via telefon eller e-post:'}
                </p>
                <div className="pl-4 border-l-2 border-petrol-medium/30 my-4 space-y-1">
                  <p>
                    <a href="tel:+4523957707" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium">
                      +45 23 95 77 07
                    </a>
                  </p>
                  <p>
                    <a href="mailto:kontakt@nordiskpsykiatri.dk" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium">
                      kontakt@nordiskpsykiatri.dk
                    </a>
                  </p>
                </div>
                <p>
                  {lang === 'da'
                    ? 'Vi bestræber os på at besvare din henvendelse hurtigst muligt og senest inden for 14 dage.'
                    : 'Vi strävar efter att besvara din förfrågan så snabbt som möjligt och senast inom 14 dagar.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 2: Hvis du ikke er tilfreds med vores svar */}
            <div className="text-left">
              <h2 className="font-display text-2xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? 'HVIS DU IKKE ER TILFREDS MED VORES SVAR' : 'OM DU INTE ÄR NÖJD MED VÅRT SVAR'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Hvis du ikke er tilfreds med vores svar eller løsning, kan du indgive en klage til Patientklagenævnet.'
                    : 'Om du inte är nöjd med vårt svar eller vår lösning kan du lämna in ett klagomål till Patientklagenævnet.'}
                </p>
                <p>
                  {lang === 'da'
                    ? 'Klagen skal indgives via klageportalen på '
                    : 'Klagomålet ska lämnas in via klagoportalen på '}
                  <a href="https://www.borger.dk" target="_blank" rel="noopener noreferrer" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium underline underline-offset-4">
                    www.borger.dk
                  </a>
                  .
                </p>
                <p>
                  {lang === 'da'
                    ? 'Du kan læse mere om dine rettigheder og klageprocessen på Patientklagenævnets hjemmeside:'
                    : 'Du kan läsa mer om dina rättigheter och klagomålsprocessen på Patientklagenævnets hemsida:'}
                </p>
                <p>
                  <a href="https://www.patientklagenaevnet.dk" target="_blank" rel="noopener noreferrer" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium underline underline-offset-4">
                    www.patientklagenaevnet.dk
                  </a>
                </p>
                <p>
                  {lang === 'da'
                    ? 'Patientklagenævnet behandler klager over private behandlere og sundhedsydelser.'
                    : 'Patientklagenævnet behandlar klagomål mot privata behandlare och hälso- och sjukvårdstjänster.'}
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
